import React, { useEffect, useState } from "react";
import { DebounceButton } from "../common/DebounceButton";
import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { useChecklistArb } from "../../hooks/use-arb";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { H4, H5, Text } from "@amzn/stencil-react-components/text";
import {
  DocUploadFormInputItem,
  DocUploadSelectDocumentDropdownConfig,
  DocUploadSelectDocumentMultiDropdownConfig,
} from "../../utility/constants/common";
import { ActionPath, DocUploadSelectOptions } from "../../utility/types/common";
import { DOC_UPLOAD_FORM_INPUT_IDS, DOC_UPLOAD_PAGE_STATE } from "../../utility/enums/common";
import { useDispatch } from "react-redux";
import {
  getIsCountryWithDownloadableContentDocUpload,
  setActivePage,
  setRequiredPagesForFileUpload,
  setSelectedFileType,
} from "../../reduxStore/slices/docUploadSlice";
import FormInputSelect from "../common/FormInputSelect";
import { getSteps } from "../../helpers/utils";
import { SUPPORTED_COUNTRY } from "../../config/appConfigConstants";
import { useAppSelector } from "../../reduxStore/reduxHooks";
import { useActionPath } from "./hooks/useActionPath";
import { TextBuilder } from "../TextBuilder";
import { getBlurbConfig } from "./configs/blurbByCountryRTW.config";
import { getCountryForDomain } from "../../utility/locale-helper";

interface SelectDocumentTypeProps {
  documentList: DocUploadSelectOptions[];
  parentDocumentList: DocUploadSelectOptions[];
  isMultiDropdown: boolean;
}

export const SelectDocumentsType = ({ documentList, parentDocumentList, isMultiDropdown }: SelectDocumentTypeProps) => {
  const [selectedParentDropdown, setSelectedParentDropdown] = useState<string>("");
  const [docUploadsFields, setDocUploadsFields] = useState(
    isMultiDropdown ? DocUploadSelectDocumentMultiDropdownConfig : DocUploadSelectDocumentDropdownConfig
  );
  const bundle = useChecklistArb();
  const actionPath = useActionPath();
  const country = getCountryForDomain();

  const isCountryWithDownloadableContent = useAppSelector(getIsCountryWithDownloadableContentDocUpload);
  const dispatch = useDispatch();

  const getInitialDocumentList = (options: DocUploadSelectOptions[], parentDocumentList: DocUploadSelectOptions[]) => {
    setDocUploadsFields(
      docUploadsFields.map((field) => {
        if (isMultiDropdown) {
          if (field.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_PARENT) {
            field.selectOptions = parentDocumentList;
          }
          if (field.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_CHILDREN) {
            field.selectOptions = options;
          }
        }
        if (field.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_SINGLE_DROPDOWN) {
          field.selectOptions = options;
        }

        return field;
      })
    );
  };

  const handleSelectChange = (
    value: DocUploadSelectOptions,
    fieldId: DOC_UPLOAD_FORM_INPUT_IDS,
    isParentDropdown?: boolean
  ) => {
    setDocUploadsFields(
      docUploadsFields.map((bgInfo) => {
        if (bgInfo.id === fieldId) {
          dispatch(setRequiredPagesForFileUpload({ pages: value.requiredPages }));
          dispatch(setSelectedFileType({ fileType: value }));

          return { ...bgInfo, value: value.value, hasError: false };
        }

        // reset the value for the children dropdown
        if (isParentDropdown && bgInfo.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_CHILDREN) {
          return { ...bgInfo, value: "", hasError: false };
        }

        return bgInfo;
      })
    );

    if (isParentDropdown) setSelectedParentDropdown(value.documentType);
  };

  const renderFormItem = (formItem: DocUploadFormInputItem) => {
    switch (formItem.type) {
      case "select":
        if (
          isMultiDropdown &&
          formItem.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_CHILDREN &&
          !selectedParentDropdown
        ) {
          return <></>;
        }

        return (
          <View
            data-testid={
              isMultiDropdown && formItem.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_CHILDREN
                ? "doc-upload-select-children"
                : isMultiDropdown && formItem.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_PARENT
                ? "doc-upload-select-parent"
                : "doc-upload-single-selection"
            }
          >
            <FormInputSelect
              key={formItem.id + formItem.value}
              inputItem={formItem}
              defaultValue={formItem.value}
              parentCategory={selectedParentDropdown}
              onChange={(option: DocUploadSelectOptions) =>
                handleSelectChange(
                  option,
                  formItem.id,
                  formItem.id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_PARENT
                )
              }
            />
          </View>
        );

      default:
        return <></>;
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;

    const _DocUploadsFields = docUploadsFields.map((field) => {
      if (field.required && field.value?.length === 0) {
        isValid = false;
        return { ...field, hasError: true };
      }

      return field;
    });

    if (!isValid) setDocUploadsFields(_DocUploadsFields);

    return isValid;
  };

  const handleClickNext = () => {
    if (!validateForm()) return;

    dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD }));
  };

  useEffect(() => {
    getInitialDocumentList(documentList, parentDocumentList);
  }, [documentList, parentDocumentList]);

  const renderSteps = () => {
    if (isCountryWithDownloadableContent) {
      return <H5>{getSteps({ bundle, currentStep: 2, maxSteps: 2 })}</H5>;
    }
  };

  const blurbConfig = getBlurbConfig(bundle);
  const headerText =
    actionPath === ActionPath.UPLOAD_DOCUMENTS_RTW
      ? bundle.getMessage("Checklist-MainPage-RightToWork-TaskTitle", "Complete your right to work documentation")
      : bundle.getMessage("Checklist-docUpload-header", "Upload mandatory documents");

  const blurbText =
    actionPath === ActionPath.UPLOAD_DOCUMENTS_RTW ? (
      <TextBuilder config={blurbConfig} country={country} />
    ) : (
      <Text>
        {bundle.getMessage(
          "Checklist-docUpload-description",
          `Please take a moment to review the acceptable documents information and ensure you have the right documents on hand before beginning the process: Click here to see examples of acceptable documents.`
        )}
      </Text>
    );

  return (
    <Col padding="S300" gridGap="S300">
      {renderSteps()}
      <H4>{headerText}</H4>
      {blurbText}
      {docUploadsFields.map((config) => (
        <Col key={config.label} gridGap={15}>
          {renderFormItem(config)}
        </Col>
      ))}
      <DebounceButton variant={ButtonVariant.Secondary} onClick={handleClickNext}>
        {bundle.getMessage("Checklist-Common-Continue", `Continue`)}
      </DebounceButton>
    </Col>
  );
};
